/**
 * Capacity (2026-10-01): open work per person, week by week, against the
 * hours they have — across every project. Backend: app/services/capacity.py.
 */
import { Fragment, useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { AlertTriangle, ChevronDown, ChevronRight, Users } from 'lucide-react'

import { useAuth } from '../contexts/AuthContext'
import { capacityService, type CapacityOverview, type CapacityPerson } from '../services/capacity'
import { apiErrorMessage } from '../utils/apiError'

const pct = (v: number | null | undefined) => (v === null || v === undefined ? '—' : `${Math.round(v * 100)}%`)

function cellClass(load: number | null): string {
  if (!load) return 'bg-muted/40 text-muted-foreground'
  if (load > 1.2) return 'bg-destructive/20 text-destructive font-semibold'
  if (load > 1.0) return 'bg-warning/20 text-warning font-semibold'
  if (load >= 0.6) return 'bg-success/20 text-foreground'
  return 'bg-success/10 text-foreground'
}

function weekLabel(iso: string): string {
  const d = new Date(`${iso}T00:00:00`)
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
}

export default function CapacityPage() {
  const { user } = useAuth()
  const canEdit = Boolean(user?.is_superuser || user?.roles?.some((r) => r.name === 'Organization Owner'))
  const [weeks, setWeeks] = useState(8)
  const [data, setData] = useState<CapacityOverview | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [open, setOpen] = useState<number | null>(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      setData(await capacityService.overview(weeks))
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not load capacity.'))
    }
  }, [weeks])

  useEffect(() => { load() }, [load])

  const saveHours = async (p: CapacityPerson, raw: string) => {
    const hours = raw.trim() === '' ? null : Number(raw)
    if (hours !== null && (Number.isNaN(hours) || hours < 0 || hours > 80)) {
      toast.error('Hours per week must be between 0 and 80.')
      return
    }
    if (hours === p.hours_per_week) return
    try {
      await capacityService.setHours(p.user_id, hours)
      toast.success(`${p.name}: ${hours ?? 40} hours a week`)
      load()
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not save hours.'))
    }
  }

  const team = data?.people.filter((p) => p.total_hours > 0 || p.overdue_hours > 0) ?? []
  const idle = data?.people.filter((p) => p.total_hours === 0 && p.overdue_hours === 0) ?? []

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-4 md:p-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold text-foreground">
            <Users className="h-6 w-6 text-primary" /> Capacity
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Open work per person, week by week, against the hours they have — across every planning and active project.
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          Show
          <select value={weeks} onChange={(e) => setWeeks(Number(e.target.value))}
            className="min-h-[44px] rounded-md border border-border bg-background px-3 text-sm text-foreground">
            <option value={4}>4 weeks</option>
            <option value={8}>8 weeks</option>
            <option value={12}>12 weeks</option>
          </select>
        </label>
      </header>

      {error && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
          {error} <button type="button" className="ml-2 underline" onClick={load}>Retry</button>
        </div>
      )}
      {!data && !error && <p className="text-sm text-muted-foreground">Loading capacity…</p>}

      {data && (
        <>
          <section className="grid grid-cols-2 gap-3 md:grid-cols-4" aria-label="Summary">
            <Kpi label="Team utilization" value={pct(data.summary.utilization)}
              hint={`${data.summary.people} people with work in the next ${weeks} weeks`} />
            <Kpi label="Over-committed" value={String(data.summary.over_allocated)}
              hint="above 100% in at least one week" tone={data.summary.over_allocated ? 'warn' : undefined} />
            <Kpi label="Below 50%" value={String(data.summary.under_used)} hint="room to take more work" />
            <Kpi label="Unassigned work" value={`${data.summary.unassigned_hours}h`}
              hint="open tasks with no owner" tone={data.summary.unassigned_hours ? 'warn' : undefined} />
          </section>

          {team.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border bg-card p-8 text-center">
              <h2 className="text-lg font-semibold text-foreground">No assigned work in this window</h2>
              <p className="mx-auto mt-2 max-w-xl text-sm text-muted-foreground">
                Capacity fills in as tasks get owners, estimates and dates. Tasks without an estimate count as 8 hours.
              </p>
            </div>
          ) : (
            <section className="overflow-x-auto rounded-lg border border-border bg-card">
              <table className="w-full min-w-[720px] text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="px-3 py-2">Person</th>
                    <th className="px-3 py-2">Hours / week</th>
                    {data.weeks.map((w) => <th key={w} className="px-2 py-2 text-center">{weekLabel(w)}</th>)}
                    <th className="px-3 py-2 text-right">Overdue</th>
                  </tr>
                </thead>
                <tbody>
                  {team.map((p) => (
                    <Fragment key={p.user_id}>
                      <tr className="border-b border-border">
                        <td className="px-3 py-2">
                          <button type="button" onClick={() => setOpen(open === p.user_id ? null : p.user_id)}
                            aria-expanded={open === p.user_id}
                            className="flex min-h-[36px] items-center gap-1 text-left font-medium text-foreground hover:underline">
                            {open === p.user_id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                            {p.name}
                            {p.over_weeks > 0 && <AlertTriangle className="h-4 w-4 text-warning" aria-label="over-committed" />}
                          </button>
                        </td>
                        <td className="px-3 py-2">
                          {canEdit ? (
                            <input type="number" min={0} max={80} defaultValue={p.hours_per_week}
                              aria-label={`Hours per week for ${p.name}`}
                              onBlur={(e) => saveHours(p, e.target.value)}
                              className="h-9 w-20 rounded-md border border-border bg-background px-2 text-sm text-foreground" />
                          ) : <span className="text-muted-foreground">{p.hours_per_week}</span>}
                        </td>
                        {p.weeks.map((c) => (
                          <td key={c.week} className="px-1 py-1">
                            <div className={`rounded px-1 py-2 text-center text-xs ${cellClass(c.load)}`}
                              title={`${c.hours}h of ${p.hours_per_week}h`}>
                              {c.load ? pct(c.load) : '—'}
                            </div>
                          </td>
                        ))}
                        <td className={`px-3 py-2 text-right ${p.overdue_hours ? 'text-destructive' : 'text-muted-foreground'}`}>
                          {p.overdue_hours ? `${p.overdue_hours}h` : '—'}
                        </td>
                      </tr>
                      {open === p.user_id && (
                        <tr className="border-b border-border bg-muted/30">
                          <td colSpan={data.weeks.length + 3} className="px-4 py-3">
                            <PersonDetail person={p} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                  {data.unassigned.total_hours > 0 && (
                    <tr className="border-b border-border">
                      <td className="px-3 py-2 font-medium text-muted-foreground">Unassigned</td>
                      <td className="px-3 py-2 text-muted-foreground">—</td>
                      {data.unassigned.weeks.map((h, i) => (
                        <td key={data.weeks[i]} className="px-2 py-2 text-center text-xs text-muted-foreground">{h ? `${h}h` : '—'}</td>
                      ))}
                      <td className="px-3 py-2 text-right text-muted-foreground">
                        {data.unassigned.overdue_hours ? `${data.unassigned.overdue_hours}h` : '—'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </section>
          )}

          {idle.length > 0 && (
            <p className="text-xs text-muted-foreground">
              No assigned work in this window: {idle.map((p) => p.name).join(', ')}.
            </p>
          )}
          <p className="text-xs text-muted-foreground">
            How it’s worked out: each open task’s estimate (8h if missing, half once in progress) is spread over the
            working days between its start and due date; overdue work counts in this week. Everyone has 40 hours a week
            unless an owner changes it here.
          </p>
        </>
      )}
    </div>
  )
}

function Kpi({ label, value, hint, tone }: { label: string; value: string; hint: string; tone?: 'warn' }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`mt-1 text-2xl font-semibold ${tone === 'warn' ? 'text-warning' : 'text-foreground'}`}>{value}</div>
      <div className="mt-1 text-xs text-muted-foreground">{hint}</div>
    </div>
  )
}

function PersonDetail({ person }: { person: CapacityPerson }) {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <div>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">By project</h3>
        <ul className="space-y-1">
          {person.projects.map((pr) => (
            <li key={pr.id} className="flex justify-between gap-2">
              <Link to={`/project/${pr.id}`} className="text-foreground hover:underline">{pr.name}</Link>
              <span className="text-muted-foreground">{pr.hours}h</span>
            </li>
          ))}
        </ul>
        {person.later_hours > 0 && (
          <p className="mt-2 text-xs text-muted-foreground">Plus {person.later_hours}h due after this window.</p>
        )}
      </div>
      <div>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Open tasks</h3>
        <ul className="space-y-1">
          {person.tasks.map((t) => (
            <li key={t.id} className="flex justify-between gap-2">
              <span className="min-w-0 truncate">
                <Link to={`/task/${t.id}`} className="text-foreground hover:underline">{t.title}</Link>
                <span className="text-muted-foreground"> · {t.project_name}</span>
              </span>
              <span className={t.overdue ? 'shrink-0 text-destructive' : 'shrink-0 text-muted-foreground'}>
                {t.overdue ? 'overdue' : t.due_date ?? 'no date'} · {t.hours}h
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}
